/**
 * Testimonials slider — assets/testimonials.js
 * Vanilla JS, no dependencies.
 * Instantiated per-section via an inline <script> in the Liquid template.
 */

(function () {
  'use strict';

  class TestimonialsSlider {
    constructor(container) {
      this.container     = container;
      this.track         = container.querySelector('.ts-track');
      this.slides        = Array.from(container.querySelectorAll('.ts-slide'));
      this.dotsContainer = container.querySelector('.ts-dots');
      this.prevBtn       = container.querySelector('.ts-arrow--prev');
      this.nextBtn       = container.querySelector('.ts-arrow--next');
      this.autoplay      = container.dataset.autoplay === 'true';
      this.autoplaySpeed = (parseInt(container.dataset.autoplaySpeed, 10) || 5) * 1000;
      this.current       = 0;
      this.timer         = null;
      this.slidesVisible = this.getSlidesVisible();
      this.dots          = [];

      this.init();
    }

    /* ── Breakpoints (match CSS) ── */
    getSlidesVisible() {
      const w = window.innerWidth;
      if (w >= 990) return 3; // desktop
      if (w >= 750) return 2; // tablet
      return 1;               // mobile
    }

    /* ── Last valid page index (0-based) ── */
    maxIndex() {
      if (this.slides.length === 0) return 0;
      // 9 slides, 3 visible  →  ceil(9/3) - 1  = 2   (pages 0, 1, 2)
      // 9 slides, 1 visible  →  ceil(9/1) - 1  = 8   (pages 0–8)
      return Math.max(0, Math.ceil(this.slides.length / this.getSlidesVisible()) - 1);
    }

    init() {
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prev());
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.next());

      this.createDots();

      /* Debounced resize — recreate dots & re-render when breakpoint changes */
      let resizeTimer;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => {
          const newVisible = this.getSlidesVisible();
          if (newVisible !== this.slidesVisible) {
            this.slidesVisible = newVisible;
            this.createDots();
            this.current = Math.min(this.current, this.maxIndex());
          }
          this.update();
        }, 100);
      });

      /* Touch / swipe */
      let startX = 0;
      this.track.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
      }, { passive: true });
      this.track.addEventListener('touchend', (e) => {
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) diff > 0 ? this.next() : this.prev();
      });

      if (this.autoplay) this.startAutoplay();
      this.update();
    }

    /* ── Build dot buttons to match page count ── */
    createDots() {
      if (!this.dotsContainer) return;
      this.dotsContainer.innerHTML = '';
      this.dots = [];

      const totalPages = this.maxIndex() + 1;
      for (let i = 0; i < totalPages; i++) {
        const dot = document.createElement('button');
        dot.classList.add('ts-dot');
        dot.setAttribute('aria-label', `Go to page ${i + 1}`);
        dot.dataset.pageIndex = i;
        dot.addEventListener('click', (e) => {
          this.goTo(parseInt(e.currentTarget.dataset.pageIndex, 10));
        });
        this.dotsContainer.appendChild(dot);
        this.dots.push(dot);
      }

      this.updateDotStates();
    }

    goTo(index) {
      this.current = Math.max(0, Math.min(index, this.maxIndex()));
      this.update();
    }

    next() {
      this.goTo(this.current >= this.maxIndex() ? 0 : this.current + 1);
      if (this.autoplay) this.restartAutoplay();
    }

    prev() {
      this.goTo(this.current <= 0 ? this.maxIndex() : this.current - 1);
      if (this.autoplay) this.restartAutoplay();
    }

    update() {
      const perView       = this.getSlidesVisible();
      const slideWidthPct = 100 / perView; // e.g. 33.333% for 3-up

      /* Set every slide's width so they exactly fill 1/N of the track */
      this.slides.forEach(slide => {
        slide.style.flex     = `0 0 ${slideWidthPct}%`;
        slide.style.maxWidth = `${slideWidthPct}%`;
      });

      /*
       * translateX uses percentages relative to the ELEMENT ITSELF (the track).
       * The track is  (slides.length * slideWidthPct)%  wide.
       * One viewport-width = perView * slideWidthPct = 100% of .ts-viewport.
       *
       * The simplest correct formula:
       *   offset = -(current * perView * slideWidthPct)%
       *          = -(current * 100)%           ← each page = one full viewport
       *
       * This is equivalent to moving exactly `perView` slides per page.
       */
      this.track.style.transform = `translateX(-${this.current * 100}%)`;

      this.updateDotStates();

      if (this.prevBtn) this.prevBtn.disabled = false;
      if (this.nextBtn) this.nextBtn.disabled = false;
    }

    updateDotStates() {
      this.dots.forEach((dot, i) => {
        const active = i === this.current;
        dot.classList.toggle('active', active);
        dot.setAttribute('aria-selected', String(active));
      });
    }

    startAutoplay()   { this.timer = setInterval(() => this.next(), this.autoplaySpeed); }
    restartAutoplay() { clearInterval(this.timer); this.startAutoplay(); }
    stopAutoplay()    { clearInterval(this.timer); }
  }

  /* Expose so the inline Liquid <script> can call: new window.TestimonialsSlider(el) */
  window.TestimonialsSlider = TestimonialsSlider;

})();
